/**
 * King Living QuantityPicker Theme
 *
 * Global quantity picker styling system:
 * - Enabled state: white background, gray border, brown plus button
 * - Disabled state: light gray background, muted colors
 * - Rounded container with minus, input, and plus buttons
 * - Buttons: 40px × 40px with 12px horizontal padding
 * - Input: 40px × 40px
 *
 * Structure supports:
 * - baseStyle: Base styles for all parts
 * - variants: Different visual styles (default, compact, etc.)
 * - sizes: Different sizes (sm, md, lg)
 * - defaultProps: Default variant and size
 *
 * @override Extends Chakra UI theme
 *
 * @example Adding a new variant:
 * ```javascript
 * variants: {
 *   default: (props) => ({ ... }),
 *   compact: (props) => ({
 *     container: { padding: '2px' },
 *     decrementButton: { height: '32px', width: '32px' },
 *     input: { height: '32px', width: '32px' },
 *     incrementButton: { height: '32px', width: '32px' },
 *   }),
 * }
 * ```
 *
 * @example Adding a new size:
 * ```javascript
 * sizes: {
 *   sm: {
 *     decrementButton: { height: '32px', width: '32px' },
 *     input: { height: '32px', width: '32px' },
 *     incrementButton: { height: '32px', width: '32px' },
 *   },
 * }
 * ```
 *
 * @example Overriding base styles:
 * ```javascript
 * // In a child package, you can override specific parts:
 * const CustomQuantityPicker = {
 *   ...baseQuantityPicker,
 *   baseStyle: (props) => ({
 *     ...baseQuantityPicker.baseStyle(props),
 *     container: {
 *       ...baseQuantityPicker.baseStyle(props).container,
 *       borderRadius: '8px', // Override border radius
 *     },
 *   }),
 * };
 * ```
 */

// Common button styles - extracted for reuse and easy override
const getButtonBaseStyles = () => ({
  _active: {bg: 'transparent'},
  _focus: {boxShadow: 'none', outline: 'none'},
  _hover: {bg: 'transparent'},
  bg: 'transparent',
  border: 'none',
  flexShrink: 0,
  fontSize: '16px',
  fontWeight: 'normal',
  lineHeight: '1.4',
  margin: 0,
  paddingInline: '12px',
  paddingY: 0,
});

// Common input styles - extracted for reuse and easy override
const getInputBaseStyles = () => ({
  _focus: {boxShadow: 'none', outline: 'none'},
  bg: 'transparent',
  border: 'none',
  flexShrink: 0,
  fontSize: '16px',
  fontWeight: '600',
  lineHeight: '1.4',
  margin: 0,
  paddingX: 0,
  paddingY: 0,
  textAlign: 'center',
  _disabled: {
    bg: 'transparent',
    color: 'brandColors.greyscale40',
    cursor: 'not-allowed',
    opacity: 1,
  },
});

/** Disabled qty stepper icon/text color (#A4A3A3). */
const qtyStepperDisabledColor = 'greyscale.40';

// Common container styles - extracted for reuse and easy override
const getContainerBaseStyles = () => ({
  alignItems: 'center',
  border: '1px solid',
  borderColor: 'brandColors.greyscale10',
  borderRadius: '4px',
  display: 'flex',
  gap: '0',
  margin: 0,
  padding: 0,
  width: 'fit-content',
});

const QuantityPicker = {
  parts: ['container', 'decrementButton', 'input', 'incrementButton'],

  baseStyle: (props) => {
    const {isDisabled = false} = props;

    return {
      container: {
        ...getContainerBaseStyles(),
        bg: isDisabled ? 'brandColors.greyscale10' : 'white',
      },
      decrementButton: {
        ...getButtonBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.greyscale50',
        height: '40px',
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            width: '40px',
            padding: 0,
          },
        },
      },
      input: {
        ...getInputBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.greyscale90',
        height: '40px',
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            width: '40px',
            padding: 0,
          },
        },
      },
      incrementButton: {
        ...getButtonBaseStyles(),
        color: isDisabled ? 'brandColors.greyscale40' : 'brandColors.primaryDarkLinen',
        height: '40px',
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            width: '40px',
            padding: 0,
          },
        },
      },
    };
  },

  // Variants for different visual styles
  variants: {
    default: (props) => {
      // Default variant - same as baseStyle
      return {};
    },
    cartFlyout: (props) => {
      const {isDisabled = false} = props;

      return {
        container: {
          alignItems: 'center',
          bg: isDisabled ? 'brandColors.greyscale10' : 'white',
          borderColor: 'greyscale90Alpha.10',
          boxSizing: 'border-box',
          flexShrink: 0,
          flexWrap: 'nowrap',
          gap: '0',
          height: '44px',
          maxWidth: '112px',
          overflow: 'hidden',
          padding: '2px',
          width: '112px',
        },
        decrementButton: {
          _active: {bg: 'transparent'},
          _focus: {boxShadow: 'none', outline: 'none'},
          _hover: {bg: 'transparent'},
          alignItems: 'center',
          bg: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          boxSizing: 'border-box',
          color: isDisabled ? qtyStepperDisabledColor : 'greyscale90Alpha.40',
          display: 'flex',
          flexShrink: 0,
          fontSize: '16px',
          fontWeight: 'normal',
          height: '40px',
          justifyContent: 'center',
          lineHeight: 1.2,
          maxHeight: '40px',
          maxWidth: '40px',
          minHeight: '40px',
          minWidth: '40px',
          p: 0,
          padding: 0,
          paddingInline: 0,
          paddingInlineEnd: 0,
          paddingInlineStart: 0,
          paddingY: 0,
          width: '40px',
          sx: {
            '&&': {
              height: '40px',
              minWidth: '40px',
              padding: 0,
            }
          },
        },
        incrementButton: {
          _active: {bg: 'transparent'},
          _focus: {boxShadow: 'none', outline: 'none'},
          _hover: {bg: 'transparent'},
          alignItems: 'center',
          bg: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          boxSizing: 'border-box',
          color: isDisabled ? qtyStepperDisabledColor : 'primary.darkLinen',
          display: 'flex',
          flexShrink: 0,
          fontSize: '16px',
          fontWeight: 'normal',
          height: '40px',
          justifyContent: 'center',
          lineHeight: 1.2,
          p: 0,
          padding: 0,
          paddingInline: 0,
          paddingInlineEnd: 0,
          paddingInlineStart: 0,
          paddingY: 0,
          width: '40px',
          sx: {
            '&&': {  
              height: '40px',
              minWidth: '40px',
              padding: 0,
            }
          },
        },
        input: {
          _focus: {boxShadow: 'none', outline: 'none'},
          bg: 'transparent',
          border: 'none',
          color: isDisabled ? qtyStepperDisabledColor : 'greyscale90Alpha.90',
          flex: '0 0 auto',
          flexShrink: 0,
          fontSize: '16px',
          fontWeight: 'normal',
          height: 'auto',
          lineHeight: 1.2,
          maxW: '25px',
          minH: 'auto',
          minW: '8px',
          pX: '2px',
          pY: '0',
          textAlign: 'center',
          w: 'auto',
          sx: {
            flex: '0 0 auto',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            maxWidth: '25px',
            minWidth: '8px',
            paddingX: '2px',
            paddingY: '0',
            width: 'auto',
          },
          _disabled: {
            bg: 'transparent',
            color: qtyStepperDisabledColor,
            cursor: 'not-allowed',
            opacity: 1,
          },
        },
      };
    },
    checkout: (props) => {
      const {isDisabled = false} = props;

      return {
        container: {
          alignItems: 'center',
          bg: isDisabled ? 'brandColors.greyscale10' : 'white',
          borderColor: 'greyscale90Alpha.10',
          boxSizing: 'border-box',
          flexShrink: 0,
          flexWrap: 'nowrap',
          gap: '0',
          height: '44px',
          maxWidth: '112px',
          overflow: 'hidden',
          padding: '2px',
          width: '112px',
        },
        decrementButton: {
          _active: {bg: 'transparent'},
          _focus: {boxShadow: 'none', outline: 'none'},
          _hover: {bg: 'transparent'},
          alignItems: 'center',
          bg: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          boxSizing: 'border-box',
          color: isDisabled ? qtyStepperDisabledColor : 'greyscale90Alpha.40',
          display: 'flex',
          flexShrink: 0,
          height: '40px',
          justifyContent: 'center',
          minHeight: '40px',
          minWidth: '40px',
          p: 0,
          width: '40px',
          sx: {'&&': {height: '40px', minWidth: '40px', padding: 0}},
        },
        incrementButton: {
          _active: {bg: 'transparent'},
          _focus: {boxShadow: 'none', outline: 'none'},
          _hover: {bg: 'transparent'},
          alignItems: 'center',
          bg: 'transparent',
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          boxSizing: 'border-box',
          color: isDisabled ? qtyStepperDisabledColor : 'primary.darkLinen',
          display: 'flex',
          flexShrink: 0,
          height: '40px',
          justifyContent: 'center',
          minHeight: '40px',
          minWidth: '40px',
          p: 0,
          width: '40px',
          sx: {'&&': {height: '40px', minWidth: '40px', padding: 0}},
        },
        input: {
          _disabled: {
            bg: 'transparent',
            color: qtyStepperDisabledColor,
            cursor: 'not-allowed',
            opacity: 1,
          },
          _focus: {boxShadow: 'none', outline: 'none'},
          bg: 'transparent',
          border: 'none',
          color: isDisabled ? qtyStepperDisabledColor : 'greyscale90Alpha.90',
          flex: '0 0 auto',
          flexShrink: 0,
          fontFeatureSettings: "'lnum' 1, 'pnum' 1",
          fontSize: {base: '14px', lg: '16px'},
          fontWeight: '600',
          height: 'auto',
          lineHeight: 1.4,
          maxW: '25px',
          minH: 'auto',
          minW: '8px',
          pX: '2px',
          pY: '0',
          textAlign: 'center',
          w: 'auto',
          sx: {
            '&&': {
              fontFeatureSettings: "'lnum' 1, 'pnum' 1",
              fontWeight: '600',
              lineHeight: '1.4',
              maxWidth: '25px',
              minWidth: '8px',
              paddingX: '2px',
              paddingY: '0',
              width: 'auto',
            },
          },
        },
      };
    },
    // Future variants can be added here:
    // compact: (props) => ({ ... }),
    // outlined: (props) => ({ ... }),
  },

  // Sizes for different dimensions
  sizes: {
    md: {
      // Default size - 40px
      container: {},
      decrementButton: {
        height: '40px',
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            width: '40px',
            padding: 0,
          },
        },
      },
      input: {
        height: '40px',
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            width: '40px',
            padding: 0,
          },
        },
      },
      incrementButton: {
        height: '40px',
        minWidth: '40px',
        sx: {
          '&&': {
            height: '40px',
            minWidth: '40px',
            padding: 0,
          },
        },
      },
    },
    cartFlyout: {
      container: {},
      decrementButton: {
        height: '40px',
        minWidth: '40px',
        paddingInline: 0,
        width: '40px',
        sx: {
          '&&': {
            height: '40px',
            minWidth: '40px',
            padding: 0,
          },
        },
      },
      input: {
        flex: '0 0 auto',
        height: 'auto',
        maxHeight: 'none',
        minHeight: 'auto',
        width: 'auto',
        sx: {
          '&&': {
            maxWidth: '25px',
            minWidth: '8px',
            paddingX: '2px',
            paddingY: '0',
            width: 'auto',
          },
        },
      },
      incrementButton: {
        height: '40px',
        minWidth: '40px',
        paddingInline: 0,
        sx: {
          '&&': {
            height: '40px',
            minWidth: '40px',
            padding: 0,
          },
        },
      },
    },
    checkout: {
      container: {},
      decrementButton: {
        height: '40px',
        minWidth: '40px',
        paddingInline: 0,
        width: '40px',
        sx: {'&&': {height: '40px', minWidth: '40px', padding: 0}},
      },
      input: {
        flex: '0 0 auto',
        height: 'auto',
        maxHeight: 'none',
        minHeight: 'auto',
        width: 'auto',
        sx: {
          '&&': {
            maxWidth: '25px',
            minWidth: '8px',
            paddingX: '2px',
            paddingY: '0',
            width: 'auto',
          },
        },
      },
      incrementButton: {
        height: '40px',
        minWidth: '40px',
        paddingInline: 0,
        sx: {'&&': {height: '40px', minWidth: '40px', padding: 0}},
      },
    },
  },

  defaultProps: {
    variant: 'default',
    size: 'md',
  },
};

export default QuantityPicker;
